import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

const quickActions = [
  {
    title: 'Company Profile',
    description: 'Set company/candidate type, logo, sender name, reply-to email, and invite branding.',
    path: '/interview-simulator/profile',
    button: 'Set Profile',
    primary: true,
  },
  {
    title: 'Question Sets',
    description: 'Upload or paste employer questions one per line and reuse them across invites.',
    path: '/interview-simulator/questions',
    button: 'Manage Questions',
    primary: true,
  },
  {
    title: 'Branded Invites',
    description: 'Generate company-branded candidate invite drafts using jobs and question sets.',
    path: '/interview-simulator/invites',
    button: 'Create Invites',
    primary: true,
  },
  {
    title: 'Jobs',
    description: 'Create roles and manage interview links for candidates.',
    path: '/interview-simulator/jobs',
    button: 'Manage Jobs',
  },
  {
    title: 'Candidates',
    description: 'View all applicants, resume availability, LinkedIn details, and report links.',
    path: '/interview-simulator/candidates',
    button: 'View Candidates',
  },
  {
    title: 'Results',
    description: 'Review completed interviews, transcripts, strengths, gaps, and full reports.',
    path: '/interview-simulator/results',
    button: 'View Results',
  },
  {
    title: 'Billing',
    description: 'Manage your plan and usage packs.',
    path: '/interview-simulator/billing',
    button: 'View Billing',
  },
];

const workflowSteps = [
  'Choose Company or Candidate profile type and save company branding.',
  'Create or paste employer-written interview question sets.',
  'Create a job and generate a branded candidate invite.',
  'Candidates complete interviews with resume and LinkedIn details attached when available.',
  'Review candidates, transcripts, reports, resumes, and LinkedIn data from the dashboard.',
];

const InterviewDashboard: React.FC = () => {
  const [stats, setStats] = useState<any>({ jobs: 0, candidates: 0, results: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [j, c, r] = await Promise.all([
          api.get(END_POINTS.interviewJobs).catch(() => []),
          api.get(END_POINTS.interviewCandidates).catch(() => []),
          api.get(END_POINTS.interviewResults).catch(() => []),
        ]);
        setStats({
          jobs: Array.isArray(j) ? j.length : (j?.results?.length || 0),
          candidates: Array.isArray(c) ? c.length : (c?.results?.length || 0),
          results: Array.isArray(r) ? r.length : (r?.results?.length || 0),
        });
      } catch (e: any) {
        console.error('Dashboard API error:', e);
        ValidationErrorToast('Error', e?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading your dashboard...</p>
        </div>
      </InterviewSidebar>
    );
  }

  return (
    <InterviewSidebar>
      <div className="row mb-4">
        <div className="col-xl-4 col-lg-6 col-md-6 mb-3">
          <div style={styles.card}>
            <h4 style={styles.statsLabel}>Active Jobs</h4>
            <h2 style={styles.statsValue}>{stats.jobs}</h2>
          </div>
        </div>
        <div className="col-xl-4 col-lg-6 col-md-6 mb-3">
          <div style={styles.card}>
            <h4 style={styles.statsLabel}>Candidates</h4>
            <h2 style={styles.statsValue}>{stats.candidates}</h2>
          </div>
        </div>
        <div className="col-xl-4 col-lg-6 col-md-6 mb-3">
          <div style={styles.card}>
            <h4 style={styles.statsLabel}>Completed Interviews</h4>
            <h2 style={styles.statsValue}>{stats.results}</h2>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5" style={{ marginBottom: '24px' }}>
          <div style={{ ...styles.card, height: '100%' }}>
            <h3 style={styles.cardTitle}>Business Interview Workflow</h3>
            <p style={{ ...styles.secondaryText, marginTop: '-8px', lineHeight: '1.7' }}>
              This dashboard now supports the full company flow: profile setup, custom questions, branded invites, candidates, resumes, transcripts, and reports.
            </p>
            <div style={{ marginTop: '18px' }}>
              {workflowSteps.map((step, index) => (
                <div key={step} style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#F0F4FF', color: '#0065FF', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{index + 1}</div>
                  <div style={{ ...styles.secondaryText, lineHeight: '1.6' }}>{step}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-7" style={{ marginBottom: '24px' }}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Quick Actions</h3>
            <div className="table-responsive">
              <table style={styles.table}>
                <tbody>
                  {quickActions.map((action) => (
                    <tr key={action.path}>
                      <td style={styles.tableCell}>
                        <strong style={styles.primaryText}>{action.title}</strong>
                        <div style={styles.secondaryText}>{action.description}</div>
                      </td>
                      <td style={{ ...styles.tableCell, textAlign: 'right' }}>
                        <Link
                          to={action.path}
                          style={{ ...styles.button, ...(action.primary ? styles.primaryButton : styles.secondaryButton), textDecoration: 'none', whiteSpace: 'nowrap' } as any}
                        >
                          {action.button}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </InterviewSidebar>
  );
};

export default InterviewDashboard;
